import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { SettingsContext, SettingsProviderProps } from "./context";
import { useTimelineContext } from "../remotion-timeline/context/use-timeline-context";
import useVideo from "../../../../../hooks/useVideo";
import { useTimelineSetup } from "./hooks/useTimelineSetup";
import {
  calculateTotalDurationInFrames,
  findSelectedItem,
  getSelectedItemSettings as getSelectedItemSettingsUtil,
} from "./utils/timeline";
import { useVideoGeneration } from "./hooks/useVideoGeneration";
import { useCoreItemSettings } from "./hooks/useCoreItemSettings";
import { useTextItemSettings } from "./hooks/useTextItemSettings";
import { useVideoItemSettings } from "./hooks/useVideoItemSettings";

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
  fonts = [],
}) => {
  const { selectedItem, changeItem, tracks, fps } = useTimelineContext();
  const { video, saveVideo } = useVideo();

  const isInitialMount = useRef(true);
  const prevTracksStringRef = useRef("");

  const { isEditorLoaded, loadEditor } = useTimelineSetup({
    video,
  });
  const loadedVideoIdRef = useRef<string | null>(null);

  const {
    generateVideo,
    displayError,
    videoDownloadUrl,
    generationProgress,
    isGenerating,
    setIsGenerating,
  } = useVideoGeneration({
    video,
    tracks,
    fps,
    saveVideo,
  });

  const selectedItemObject = findSelectedItem(tracks, selectedItem);

  const [applyToAllMap, setApplyToAllMap] = useState<Record<string, boolean>>(
    {}
  );

  const applyToAll = selectedItem ? applyToAllMap[selectedItem] ?? true : true;

  const getSelectedItemSettings = useCallback(() => {
    return getSelectedItemSettingsUtil(selectedItemObject);
  }, [selectedItemObject]);

  const {
    updatePositionSettings,
    updateRotationSettings,
    updateSizeSettings,
    updateBackgroundSettings,
  } = useCoreItemSettings({
    selectedItem,
    applyToAll,
    tracks,
    changeItem,
    selectedItemObject,
  });

  const { setFilterValue } = useVideoItemSettings({
    selectedItemObject,
    changeItem,
  });

  const {
    updateFontSettings,
    updateAlignmentSettings,
    updateStrokeSettings,
    updateContentSettings,
    addTextItem,
  } = useTextItemSettings({
    selectedItemObject,
    applyToAll,
  });

  const [bottomPanelHeight, setBottomPanelHeight] = useState<number>(0);

  useEffect(() => {
    if (video.id && video.id !== loadedVideoIdRef.current) {
      loadEditor();
      loadedVideoIdRef.current = video.id;
    }
  }, [video.id, loadEditor]);

  const setApplyToAllForCurrentItem = (value: boolean) => {
    if (!selectedItem) return;

    setApplyToAllMap((prev) => ({
      ...prev,
      [selectedItem]: value,
    }));
  };

  const debouncedSave = useRef(
    debounce((tracksToSave, videoId) => {
      const calculatedDuration = calculateTotalDurationInFrames(tracksToSave);

      const config = {
        durationInFrames: calculatedDuration,
        fps,
      };

      saveVideo(config, tracksToSave, videoId);
    }, 5000)
  ).current;

  useEffect(() => {
    if (!video.id || tracks?.length === 0) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevTracksStringRef.current = JSON.stringify(tracks);
      return;
    }

    const currentTracksString = JSON.stringify(tracks);
    if (prevTracksStringRef.current !== currentTracksString) {
      debouncedSave(tracks, video.id);
      prevTracksStringRef.current = currentTracksString;
    }
  }, [tracks, debouncedSave, video.id]);

  return (
    <SettingsContext.Provider
      value={{
        isEditorLoaded,
        loadEditor,
        applyToAll,
        setApplyToAllForCurrentItem,
        applyToAllMap,
        updateContentSettings,
        updatePositionSettings,
        getSelectedItemSettings,
        updateRotationSettings,
        updateFontSettings,
        updateStrokeSettings,
        updateBackgroundSettings,
        selectedItemId: selectedItem,
        updateAlignmentSettings,
        updateSizeSettings,
        bottomPanelHeight,
        setBottomPanelHeight,
        isGenerating,
        setIsGenerating,
        generateVideo,
        displayError,
        generationProgress,
        videoDownloadUrl,
        fonts,
        setFilterValue,
        addTextItem,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
